import { AppBar, Tab } from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";

import Tabs from "@/components/atoms/ZoomAwareTabs";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { OrderFormData, OrderTabs } from "@/components/screens/OrderFormPage/types";
import ROUTE_URLS from "@/constants/route-urls";
import useAuth from "@/contexts/auth-context";
import useLanguage from "@/contexts/language-context";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import useOrderStatusLogic from "@/hooks/use-order-status-logic";
import useTabs from "@/hooks/use-tabs";
import { IOrder } from "@/interfaces/order.interfaces";
import { OrderFormPageMessages } from "@/messages/order-form-page/order-form-page.messages";
import pxToRem from "@/themes/functions/pxToRem";

import CommentsDataTab from "./components/commentsTab";
import HistoryDataTab from "./components/historyTab";
import ItemDataTab from "./components/ItemsTab";
import OrderDataTab from "./components/orderDataTab";
import PaymentDataTab from "./components/paymentsTab";
import { TABS_ITEMS } from "./constants";
import { buildFormSchema, buildInitialData } from "./form-data";
import { getExpectedOrderAmount, getOrderItemTotals } from "./components/ItemsTab/form-data";

type Props = {
  onSubmit: (data: OrderFormData) => void;
  order: IOrder | null;
  isEditing: boolean;
  messages: OrderFormPageMessages;
  isLoading: boolean;
};

const OrderForm: React.FC<Props> = ({ order, isEditing, messages, onSubmit, isLoading }) => {
  const TABS = TABS_ITEMS(messages, isEditing);
  const tabs = useTabs<OrderTabs>({ initialTab: "orderDataTab", tabs: TABS, isEditing });
  const { activeTab, changeTab, isTabDisabled, handleBack, tabsOrientation } = tabs;
  const { user } = useAuth();
  const customerId = useGetCustomerId();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { disabledButton } = useOrderStatusLogic(order, isEditing);

  const isOrderDataTab = activeTab === "orderDataTab";
  const isItemDataTab = activeTab === "itemDataTab";
  const isPaymentDataTab = activeTab === "paymentDataTab";
  const isHistoryTab = activeTab === "historyTab" && isEditing;
  const isCommentsTab = activeTab === "commentsDataTab" && isEditing;

  const tabSize = isEditing ? 650 : 390;

  const steps: OrderTabs[] = ["orderDataTab", "itemDataTab", "paymentDataTab"];

  const handleSubmitForm = (values: OrderFormData, actions: FormikHelpers<OrderFormData>) => {
    const { netTotal, couponDiscount } = getOrderItemTotals(values.items);
    const payload: OrderFormData = {
      ...values,
      orderAmount: netTotal,
      discountCouponAmount: couponDiscount,
      totalAmount: getExpectedOrderAmount({
        ...values,
        orderAmount: netTotal,
        discountCouponAmount: couponDiscount,
      }),
    };

    if (isEditing) {
      onSubmit(payload);
    } else {
      const currentStepIndex = steps.indexOf(activeTab);

      if (!isPaymentDataTab) {
        // Move to next step
        const nextStep = steps[currentStepIndex + 1];
        changeTab(nextStep);
        actions.setTouched({});
        actions.setSubmitting(false);
      } else {
        onSubmit(payload);
      }
    }
  };

  const { updateButton, cancelButton, backButton, continueButton } = messages;
  const submitBtn = isEditing ? updateButton : isPaymentDataTab ? updateButton : continueButton;
  const isCancelButtonVisible = isEditing || (isOrderDataTab && !isEditing);
  const cancelOrBackBtn = isCancelButtonVisible ? cancelButton : backButton;

  const handleCancelOrBack = () => {
    if (isEditing) {
      navigate(-1);
    } else if (isOrderDataTab) {
      navigate(ROUTE_URLS.orders(lang));
    } else {
      handleBack();
    }
  };

  if (!customerId) return;

  return (
    <Formik
      initialValues={buildInitialData(Number(customerId), order, user, isEditing, disabledButton)}
      validationSchema={buildFormSchema(messages, activeTab, isEditing)}
      onSubmit={handleSubmitForm}
      validateOnMount={false}
      validateOnBlur
      validateOnChange={false}
    >
      {() => {
        return (
          <Form noValidate>
            <AppBar position="static">
              <Tabs
                orientation={tabsOrientation}
                value={activeTab}
                onChange={(_, newTab) => {
                  if (!isTabDisabled(newTab)) changeTab(newTab);
                }}
                sx={{ maxWidth: pxToRem(tabSize), width: { xs: "100%" } }}
              >
                {TABS.map((tab, index) => (
                  <Tab
                    key={index}
                    label={tab.label}
                    value={tab.value}
                    disabled={isTabDisabled(tab.value)}
                  />
                ))}
              </Tabs>
            </AppBar>

            {isOrderDataTab && <OrderDataTab messages={messages} order={order} />}
            {isItemDataTab && <ItemDataTab messages={messages} order={order} />}
            {isPaymentDataTab && <PaymentDataTab messages={messages} order={order} />}
            {isCommentsTab && <CommentsDataTab messages={messages} />}
            {isHistoryTab && <HistoryDataTab messages={messages} order={order} />}

            {!isHistoryTab && (
              <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
                <MDButton
                  variant="gradient"
                  color="light"
                  onClick={handleCancelOrBack}
                  disabled={isLoading}
                >
                  {cancelOrBackBtn}
                </MDButton>

                <MDButton
                  variant="gradient"
                  type="submit"
                  color="dark"
                  isLoading={isLoading}
                  disabled={disabledButton && activeTab !== "orderDataTab"}
                >
                  {submitBtn}
                </MDButton>
              </MDBox>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default OrderForm;
